import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import IconButton from '@mui/material/IconButton'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import {
    Applications,
    RoutePaths,
    UserPermissions,
    drawerWidth,
} from '../../../constants/constants'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { updateNavToggle } from '../../../redux/slices/navSlice'
import SubNavList from './SubNavList'
import TruncatedText from '../../Common/TruncatedText'
import { getNoHideEntityList } from '../../../utils/NavUtils'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { usePermissions } from '../../../hooks/usePermissions'
import type { EntityType } from '../../DTStudio/interfaces'
import {
    Fragment,
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { NavigateNextSharp } from '@mui/icons-material'
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesProvider'

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    top: 80,
    marginLeft: '2px',
    border: '1px solid #959595',
    borderRadius: '8px',
    height: 'calc(100vh - 83px)',
    overflowY: 'hidden',
    '&: hover': {
        overflowY: 'auto',
    },
})

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',

    // width: `calc(${ theme.spacing(7) } + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)})`,
    },
    top: 80,
    marginLeft: '2px',
    border: '1px solid #959595',
    borderRadius: '8px',
    marginBottom: '20px',
    height: 'calc(100vh - 83px)',
    overflowY: 'hidden',
    '&: hover': {
        overflowY: 'auto',
    },
})

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    maxHeight: 0,
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}))

export interface NavItemsType {
    label: string
    path: string
    id: string
    disabled: boolean
    permissions: UserPermissions[]
    hasPermission: boolean
    displaySeparator?: boolean
    logo?: string
    hasSubMenu?: boolean
    paths?: string[]
    subEntityTypes?: EntityType[]
}

export default function SideNav(): JSX.Element {
    const { t } = useTranslation()
    const [version, setVersion] = useState<string>('1.1.0')
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [anchorElSubNav, setAnchorElSubNav] = useState<null | HTMLElement>(
        null,
    )
    const [activeSubNavId, setActiveSubNavId] = useState<string | null>(null)
    const currentYear = useMemo(() => {
        return dayjs().year()
    }, [])
    const topNavItems: NavItemsType[] = useMemo(
        () => [
            {
                label: t(Applications.inTransit.label),
                id: Applications.inTransit.id,
                path: RoutePaths.ccl,
                paths: [RoutePaths.ccl],
                disabled: false,
                displaySeparator: false,
                logo: '/assets/images/icons/location-icon.svg',
                permissions: [UserPermissions.ENTITY_READER],
                hasPermission: false,
            },
            {
                label: t(Applications.facilityMonitoring.label),
                id: Applications.facilityMonitoring.id,
                path: RoutePaths.facilityMonitoring,
                paths: [RoutePaths.facilityMonitoring],
                disabled: false,
                displaySeparator: false,
                logo: '/assets/images/icons/cpu-setting-icon.svg',
                permissions: [UserPermissions.ENTITY_READER],
                hasPermission: false,
            },
            {
                label: t(Applications.analytics.label),
                id: Applications.analytics.id,
                path: RoutePaths.analytics,
                paths: [RoutePaths.analytics, RoutePaths.analyticsPreview],
                disabled: false,
                displaySeparator: false,
                logo: '/assets/images/icons/BusinessIntelligence.svg',
                permissions: [UserPermissions.ENTITY_ANALYST],
                hasPermission: false,
            },
            // {
            //     label: Applications.flashtrak.label,
            //     id: Applications.flashtrak.id,
            //     path: RoutePaths.flashtrak,
            //     paths: [RoutePaths.flashtrak],

            //     logo: '/assets/images/icons/Truck-Icon.svg',
            //     disabled: false,
            //     displaySeparator: false,
            //     permissions: [UserPermissions.ENTITY_READER],
            //     hasPermission: false,
            // },

            {
                label: t(Applications.deviceManager.label),
                id: Applications.deviceManager.id,
                path: RoutePaths.deviceManager,
                paths: [RoutePaths.deviceManager],
                disabled: false,
                displaySeparator: false,
                logo: '/assets/images/icons/deviceManager.svg',
                permissions: [
                    UserPermissions.ORG_ADMIN,
                    UserPermissions.USER_ADMIN,
                ],
                hasPermission: false,
            },
            {
                label: t(Applications.uploadManager.label),
                id: Applications.uploadManager.id,
                path: RoutePaths.uploadManager,
                paths: [RoutePaths.uploadManager],
                disabled: false,
                displaySeparator: false,
                logo: '/assets/images/icons/uploadManager.svg',
                permissions: [
                    UserPermissions.ORG_ADMIN,
                    UserPermissions.SYSTEM_READER,
                ],
                hasPermission: false,
            },
        ],
        [t],
    )
    const navItems: NavItemsType[] = useMemo(
        () => [
            {
                label: t('networkAdmin'),
                id: 'Network Admin',
                path: RoutePaths.networkAdmin,
                paths: [RoutePaths.networkAdmin],
                logo: '/assets/images/icons/DTStudio-Icon.svg',
                displaySeparator: true,
                disabled: false,
                permissions: [
                    UserPermissions.ORG_ADMIN,
                    UserPermissions.SYSTEM_ADMIN,
                    UserPermissions.SYSTEM_READER,
                ],
                hasPermission: false,
            },
            {
                label: t('networkUsers'),
                id: 'Network Users',
                path: RoutePaths.networkUsers,
                paths: [
                    RoutePaths.networkUsers,
                    RoutePaths.organizations,
                    RoutePaths.userGroups,
                    RoutePaths.userManagement,
                    RoutePaths.userGroup,
                    RoutePaths.programs,
                ],
                hasSubMenu: true,
                logo: '/assets/images/icons/Setup-Icon.svg',
                disabled: false,
                displaySeparator: false,
                permissions: [
                    UserPermissions.ORG_ADMIN,
                    UserPermissions.USER_ADMIN,
                    UserPermissions.SYSTEM_ADMIN,
                    UserPermissions.SYSTEM_READER,
                ],
                hasPermission: false,
            },
        ],
        [t],
    )

    const theme = useTheme()
    const navigate = useNavigate()
    const { guard } = useUnsavedChanges()
    const { hasPermission: hasPermissionAccess } = usePermissions()
    const orgSlice = useAppSelector((state) => state.org)
    const { permissions = [], enabledApplications = [] } = orgSlice
    const open = useAppSelector((state) => state.nav.isOpen)
    const navList = useAppSelector((state) => state.nav.navList)
    const dispatch = useAppDispatch()

    const openNetworkUserLinks = Boolean(anchorEl)
    const menuOffSet = open ? drawerWidth : 100
    const vectorIconSrc = open
        ? '/assets/images/icons/Vector-Icon-Left.svg'
        : '/assets/images/icons/Vector-Icon-Right.svg'
    const route = useLocation()

    const encodedUrl = route.pathname
    const routePathname = encodedUrl.replace(/%20/g, ' ')
    const hasApplicationsAccess = useMemo(
        () =>
            hasPermissionAccess([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermissionAccess],
    )
    const showUserManagementLink = useMemo(
        () =>
            hasPermissionAccess([
                UserPermissions.USER_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermissionAccess],
    )
    const showOrganizationsLink = useMemo(
        () =>
            hasPermissionAccess([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermissionAccess],
    )
    const sideNavItems: NavItemsType[] = useMemo(() => {
        let updatedEntityList: NavItemsType[] = []

        if (navList?.length) {
            const updatedNoHideEntityList = getNoHideEntityList(navList)

            updatedEntityList = updatedNoHideEntityList.map(
                (item) =>
                    ({
                        label: item.name,
                        path: `/entity/${item.name}/${item.id}`,
                        paths: ['/entity/'],
                        id: item.id,
                        logo: item.logo,
                        subEntityTypes: item.subEntityTypes,
                        disabled: false,
                        permissions: [
                            UserPermissions.ENTITY_ANALYST,
                            UserPermissions.ENTITY_WRITER,
                            UserPermissions.ENTITY_READER,
                        ],
                        hasPermission: false,
                    }) as unknown as NavItemsType,
            )

            if (updatedEntityList.length) {
                updatedEntityList[0].displaySeparator = true
            }
        }

        const updated = [...topNavItems, ...updatedEntityList, ...navItems]
        if (!permissions?.length) return updated

        return updated.map((i) => {
            if (i.id === Applications.inTransit.id) {
                /**
                 * This logic ensures that for non admins, if the application is not enabled then they can't access the
                 * application
                 */
                const hasPermission =
                    enabledApplications?.includes(Applications.inTransit.id) ||
                    hasApplicationsAccess
                return { ...i, hasPermission }
            } else if (i.id === Applications.facilityMonitoring.id) {
                /**
                 * This logic ensures that for non admins, if the application is not enabled then they can't access the
                 * application
                 */
                const hasPermission =
                    enabledApplications?.includes(
                        Applications.facilityMonitoring.id,
                    ) || hasApplicationsAccess

                return { ...i, hasPermission }
            } else if (i.id === Applications.analytics.id) {
                /**
                 * This logic ensures that for non analyst, if the application is not enabled then they can't access the
                 * application
                 */
                const hasPermission =
                    (enabledApplications?.includes(Applications.analytics.id) &&
                        hasPermissionAccess(UserPermissions.ENTITY_ANALYST)) ||
                    hasApplicationsAccess

                return { ...i, hasPermission }
            } else if (i.id === Applications.deviceManager.id) {
                /**
                 * This logic ensures that for non admins, if the application is not enabled then they can't access the
                 * application
                 */
                const hasPermission =
                    (enabledApplications?.includes(
                        Applications.deviceManager.id,
                    ) &&
                        (hasPermissionAccess(UserPermissions.USER_ADMIN) ||
                            hasPermissionAccess(
                                UserPermissions.DEVICE_WRITER,
                            ))) ||
                    hasApplicationsAccess
                return { ...i, hasPermission }
            } else {
                const hasPermission = i.permissions.some((p) =>
                    permissions.includes(p),
                )
                // adding saparator based on user permissions
                let displaySeparator = i.displaySeparator
                if (permissions.includes(UserPermissions.ORG_ADMIN)) {
                    if (i.path === RoutePaths.networkAdmin) {
                        displaySeparator = true //adding the separator, if the user is an org admin
                    }
                } else if (
                    permissions.includes(UserPermissions.USER_ADMIN) //adding the separator, if the user is an user admin
                ) {
                    if (i.path === RoutePaths.networkUsers) {
                        displaySeparator = true
                    }
                }
                return { ...i, hasPermission, displaySeparator }
            }
        })
    }, [
        topNavItems,
        navItems,
        navList,
        permissions,
        enabledApplications,
        hasApplicationsAccess,
        hasPermissionAccess,
    ])

    useEffect(() => {
        fetch('/version.json')
            .then((res) => res.json())
            .then((data) => {
                setVersion(data.version)
            })
            .catch(() => {
                setVersion('1.1.0')
            })
    }, [])
    const handleDrawerOpen = () => {
        dispatch(updateNavToggle(!open))
    }

    const handleNavigation = useCallback(
        (path: string) => {
            /**
             * Every side-nav destination funnels through here, so guarding this one
             * call covers all menu items: if an in-page form has unsaved edits the
             * user is asked to confirm before we navigate away (UBQ-5259, Issue 3).
             */
            guard(() => {
                startTransition(() => {
                    // navigating the pages
                    navigate(path)
                })
            })
        },
        [navigate, guard],
    )

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSubMenuClick = (path: string) => {
        handleClose()
        handleNavigation(path)
    }

    const handleClickSubNav = (
        event: React.MouseEvent<HTMLElement>,
        id: string,
    ) => {
        setAnchorElSubNav(event.currentTarget)
        setActiveSubNavId((prev) => (prev === id ? null : id))
    }
    const handleCloseSubNav = useCallback(() => {
        setAnchorElSubNav(null)
        setActiveSubNavId(null)
    }, [])

    useEffect(() => {
        const handleDocumentMouseDown = (event: MouseEvent) => {
            if (!activeSubNavId) return

            const target = event.target as Element | null

            if (
                target?.closest(
                    '[data-sub-nav-menu="true"], [data-sub-nav-trigger="true"]',
                )
            ) {
                return
            }

            handleCloseSubNav()
        }

        document.addEventListener('mousedown', handleDocumentMouseDown)

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown)
        }
    }, [activeSubNavId, handleCloseSubNav])

    return (
        <Box sx={{ display: 'flex', position: 'fixed' }} id="SideNav">
            <Drawer variant="permanent" open={open}>
                <Toolbar
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: open ? 'end' : 'start',
                    }}
                >
                    <IconButton
                        color="inherit"
                        // aria-label={open ? t('collapseSidebarNavigation') : t('expandSidebarNavigation')}
                        aria-expanded={open}
                        // title={open ? t('collapseSidebarNavigation') : t('expandSidebarNavigation')}
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            justifyContent: 'end',
                        }}
                        id="vector-icon"
                    >
                        <img
                            src={vectorIconSrc}
                            // alt={open ? t('collapseSidebarNavigationArrow') : t('expandSidebarNavigationArrow')}
                            alt=""
                            loading="lazy"
                            width={'25px'}
                            height={'25px'}
                        />
                    </IconButton>
                </Toolbar>
                <List>
                    {sideNavItems
                        .filter((item) => item.hasPermission)
                        .map((item: NavItemsType, index: number) => {
                            const isCurrentPath =
                                item.paths?.includes(routePathname) ||
                                item.path === routePathname
                            return (
                                <Fragment key={`${item.id}_${index}`}>
                                    {item.displaySeparator && (
                                        <Box
                                            component="li"
                                            sx={{
                                                padding: '20px 15px 20px 15px',
                                            }}
                                            key={item.label + index}
                                        >
                                            <Divider
                                                sx={{
                                                    borderBottomWidth: 2,
                                                    color: '#959595',
                                                }}
                                            />
                                        </Box>
                                    )}
                                    <ListItem
                                        key={item.label}
                                        disablePadding
                                        sx={{
                                            display: 'flex',
                                            filter: isCurrentPath
                                                ? 'none'
                                                : 'brightness(0)',
                                            opacity: isCurrentPath
                                                ? '1'
                                                : '0.6',

                                            '&::after': {
                                                content: '""',
                                                borderRight: isCurrentPath
                                                    ? `4px solid ${theme.palette.primary.main}`
                                                    : '',
                                                height: '42px',
                                                position: 'absolute',
                                                right: 0,
                                                width: open
                                                    ? 'calc(100% - 25px)'
                                                    : 'cacl(100%)',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                    >
                                        <ListItemButton
                                            sx={{
                                                minHeight: 48,
                                                justifyContent: open
                                                    ? 'initial'
                                                    : 'center',
                                                paddingLeft: '16px',
                                                paddingRight: '0px',
                                                cursor: item.disabled
                                                    ? 'not-allowed'
                                                    : 'pointer',
                                                zIndex: 2,
                                            }}
                                            disabled={item.disabled}
                                            id={item.label}
                                            onClick={(event) => {
                                                if (item.disabled) {
                                                    return
                                                } else if (item.hasSubMenu) {
                                                    handleClick(event)
                                                } else {
                                                    handleNavigation(item.path)
                                                }
                                            }}
                                        >
                                            <Tooltip
                                                title={
                                                    open ? '' : t(item.label)
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        width: '24px',
                                                        minWidth: '24px',
                                                        maxWidth: '24px',
                                                        height: '24px',
                                                        mr: open
                                                            ? '10px'
                                                            : 'auto',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    {item.logo ? (
                                                        <img
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                            }}
                                                            src={item.logo}
                                                            loading="lazy"
                                                            alt={t(
                                                                'navigationIconFor',
                                                                {
                                                                    label: item.label,
                                                                },
                                                            )}
                                                        />
                                                    ) : (
                                                        <img
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                            }}
                                                            src="/assets/images/icons/Truck-Icon.svg"
                                                            loading="lazy"
                                                            alt={t(
                                                                'truckNavigationIcon',
                                                            )}
                                                        />
                                                    )}
                                                </ListItemIcon>
                                            </Tooltip>
                                            {open ? (
                                                <TruncatedText
                                                    variant="h6"
                                                    text={t(item.label)}
                                                />
                                            ) : null}
                                        </ListItemButton>
                                        {open && item.subEntityTypes?.length ? (
                                            <ListItemIcon
                                                data-sub-nav-trigger="true"
                                                sx={{
                                                    minHeight: 48,
                                                    minWidth: 24,
                                                    paddingLeft: '10px',
                                                    paddingRight: '20px',
                                                    cursor: item.disabled
                                                        ? 'not-allowed'
                                                        : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    zIndex: 2,
                                                }}
                                                onClick={(event) => {
                                                    handleClickSubNav(
                                                        event,
                                                        item.id,
                                                    )
                                                }}
                                                id={`${item.label}-sub-menu-button`}
                                            >
                                                <NavigateNextSharp />
                                            </ListItemIcon>
                                        ) : null}
                                        {item.subEntityTypes?.length ? (
                                            <SubNavList
                                                anchorElSubNav={anchorElSubNav}
                                                handleCloseSubNav={
                                                    handleCloseSubNav
                                                }
                                                subNavOpen={
                                                    activeSubNavId === item.id
                                                }
                                                list={item.subEntityTypes}
                                            />
                                        ) : null}
                                    </ListItem>
                                </Fragment>
                            )
                        })}
                    {open ? (
                        <ListItem>
                            <Typography
                                sx={{
                                    bottom: '10px',
                                    width: '100%',
                                    display: 'flex',
                                    color: '#555',
                                }}
                                id={'UBQ-network-version'}
                            >
                                &copy; {currentYear} UBQ.Network v{version}
                            </Typography>
                        </ListItem>
                    ) : null}
                </List>
            </Drawer>

            {openNetworkUserLinks ? (
                <Menu
                    id="setup-sub-menu"
                    aria-label={t('setupSubMenu')}
                    aria-labelledby={
                        anchorEl
                            ? anchorEl.id || 'setup-sub-menu-button'
                            : 'setup-sub-menu-button'
                    }
                    anchorEl={anchorEl}
                    open={openNetworkUserLinks}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: menuOffSet + 200,
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 233,
                    }}
                >
                    {showUserManagementLink && (
                        <MenuItem
                            id="user-mgmt"
                            onClick={() =>
                                handleSubMenuClick(RoutePaths.userManagement)
                            }
                            aria-label={t('userManagement')}
                            title={t('userManagement')}
                            sx={{
                                filter:
                                    routePathname === RoutePaths.userManagement
                                        ? 'none'
                                        : 'brightness(0)',
                                opacity:
                                    routePathname === RoutePaths.userManagement
                                        ? '1'
                                        : '0.7',
                            }}
                        >
                            <Typography variant="h6">
                                {t('userManagement')}
                            </Typography>
                        </MenuItem>
                    )}
                    {showUserManagementLink && (
                        <MenuItem
                            id="user-groups"
                            onClick={() =>
                                handleSubMenuClick(RoutePaths.userGroups)
                            }
                            aria-label={t('userGroups')}
                            title={t('userGroups')}
                            sx={{
                                filter:
                                    routePathname === RoutePaths.userGroups ||
                                    routePathname.includes('/user-group/')
                                        ? 'none'
                                        : 'brightness(0)',
                                opacity:
                                    routePathname === RoutePaths.userGroups ||
                                    routePathname.includes('/user-group/')
                                        ? '1'
                                        : '0.7',
                            }}
                        >
                            <Typography variant="h6">
                                {t('userGroups')}
                            </Typography>
                        </MenuItem>
                    )}
                    {showOrganizationsLink && (
                        <MenuItem
                            id="organizations"
                            onClick={() =>
                                handleSubMenuClick(RoutePaths.organizations)
                            }
                            aria-label={t('organizations')}
                            title={t('organizations')}
                            sx={{
                                filter:
                                    routePathname === RoutePaths.organizations
                                        ? 'none'
                                        : 'brightness(0)',
                                opacity:
                                    routePathname === RoutePaths.organizations
                                        ? '1'
                                        : '0.7',
                            }}
                        >
                            <Typography variant="h6">
                                {t('organizations')}
                            </Typography>
                        </MenuItem>
                    )}
                    {showOrganizationsLink && (
                        <MenuItem
                            id="programs"
                            onClick={() =>
                                handleSubMenuClick(RoutePaths.programs)
                            }
                            aria-label={t('programs')}
                            title={t('programs')}
                            sx={{
                                filter:
                                    routePathname === RoutePaths.programs
                                        ? 'none'
                                        : 'brightness(0)',
                                opacity:
                                    routePathname === RoutePaths.programs
                                        ? '1'
                                        : '0.7',
                            }}
                        >
                            <Typography variant="h6">
                                {t('programs')}
                            </Typography>
                        </MenuItem>
                    )}
                </Menu>
            ) : null}
        </Box>
    )
}
